// Basic-mode presets: each one resolves to the config fields the Advanced form would otherwise ask for.
// Pure on purpose (the clock is injected) so the date math is unit-testable.

export const EDITOR_PRESETS = [
  { id: 'LAST_MONTH', label: 'Último mes', months: 1, durationSeconds: 15 },
  { id: 'LAST_3_MONTHS', label: 'Últimos 3 meses', months: 3, durationSeconds: 30 },
  { id: 'LAST_6_MONTHS', label: 'Últimos 6 meses', months: 6, durationSeconds: 60 },
  { id: 'LAST_YEAR', label: 'Último año', months: 12, durationSeconds: 90 },
  { id: 'ALL_TIME', label: 'Todo el historial', months: null, durationSeconds: 90 },
];

const pad = (n) => String(n).padStart(2, '0');

// Same shape as the <input type="datetime-local"> value the Advanced form stores.
const toDateTimeLocal = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const findPreset = (id) => EDITOR_PRESETS.find((preset) => preset.id === id);

// Local midnight N months before `now`, clamping the day (Mar 31 - 1 month -> Feb 28).
export const presetStartDate = (id, now = new Date()) => {
  const preset = findPreset(id);
  if (!preset || preset.months === null) return null;
  const start = new Date(now.getFullYear(), now.getMonth() - preset.months, 1);
  const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  start.setDate(Math.min(now.getDate(), lastDay));
  return start;
};

// Browser IANA zone, fallback for the time window; null lets the backend default (UTC) apply.
const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || null;

// `timeZone` is the camera site's IANA zone; the browser zone is only the fallback when it is unknown.
export const buildPresetConfig = (id, now = new Date(), timeZone = null) => {
  const preset = findPreset(id);
  if (!preset) return null;
  const start = presetStartDate(id, now);
  return {
    period: start ? 'CUSTOM' : 'COMPLETO',
    customStart: start ? toDateTimeLocal(start) : '',
    customEnd: start ? toDateTimeLocal(now) : '',
    durationSeconds: String(preset.durationSeconds),
    temporalBlend: false,
    intervalMinutes: '15',
    timeOfDayStart: '11:00',
    timeOfDayEnd: '14:00',
    timeZone: timeZone || browserTimeZone(),
  };
};

// "Último mes" se habilita aunque la cámara tenga menos de un mes de fotos si
// ya juntó al menos este mínimo (el video sale desde la primera foto).
export const MIN_HISTORY_DAYS_LAST_MONTH = 20;

const historyDays = (firstImageDate, now) =>
  Math.floor((now.getTime() - new Date(`${firstImageDate}T00:00:00`).getTime()) / 86400000);

// `firstImageDate` is the 'YYYY-MM-DD' of the oldest photo. Unknown history fails open; all time is always on.
export const isPresetAvailable = (id, firstImageDate, now = new Date()) => {
  const start = presetStartDate(id, now);
  if (!start || !firstImageDate) return true;
  if (firstImageDate <= toDateTimeLocal(start).slice(0, 10)) return true;
  return findPreset(id)?.months === 1 && historyDays(firstImageDate, now) >= MIN_HISTORY_DAYS_LAST_MONTH;
};

// True when the preset is usable but the camera history is shorter than it
// (the picker then says "desde el dd/mm").
export const isPresetPartial = (id, firstImageDate, now = new Date()) => {
  const start = presetStartDate(id, now);
  if (!start || !firstImageDate) return false;
  return firstImageDate > toDateTimeLocal(start).slice(0, 10) && isPresetAvailable(id, firstImageDate, now);
};
